import { omit } from "lodash-es";
import { type FC } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { toastSuccessMessage } from "../../utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "../../utils/amplitude";

import { LabeledTextInput } from "./LabeledTextInput";
import { LabeledTextarea } from "./LabeledTextarea";
import { SubmitOrCancelButtons } from "./SubmitOrCancelButtons";

import { LMSConnectionIndicator } from "components/materials/section/LMSConnectionIndicator";
import { showUnlinkConfirmationModal } from "components/modals/UnlinkSectionModal";
import { t } from "i18n/i18n";
import { LinkButton } from "mds/components/LinkButton";
import { Tooltip } from "mds/components/Tooltip";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import { selectDesignatedCourse, selectLmsType } from "store/selectors";
import { useCourseSettingActions } from "utils/section";

type FormValues = {
  title: string;
  description: string;
  external_section_id: string;
};

type CourseSettingsCourseInfoFormProps = {
  className?: string;
  onClose: () => void;
};

export const CourseSettingsCourseInfoForm: FC<CourseSettingsCourseInfoFormProps> = ({
  className,
  onClose,
}) => {
  const currentCourse = useAppSelector(selectDesignatedCourse);
  const currentLmsType = useAppSelector(selectLmsType);
  const { canAuthorOrg } = useCapabilities();

  const form = useForm<FormValues>({
    defaultValues: {
      title: currentCourse?.title,
      description: currentCourse?.description || "",
      external_section_id: currentCourse?.external_section_id || "",
    },
  });

  const { handleCourseDelete } = useCourseSettingActions();

  if (!currentCourse) {
    return null;
  }

  const onHandleCourseUnlink = async () => {
    if (!currentCourse.lms_section_id || !currentLmsType) return;
    onClose();
    await showUnlinkConfirmationModal({
      courseId: currentCourse.id,
      lmsType: currentLmsType,
    });
  };

  const onHandleCourseDelete = () => {
    onClose();
    handleCourseDelete(currentCourse);
  };

  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    trackEvent("Course Settings - Save changes", BUTTON_EVENT_CATEGORY);
    const dataWithoutEmptyStrings = {
      ...formData,
      description: formData.description || null,
      external_section_id: formData.external_section_id || null,
    };
    const data = canAuthorOrg
      ? dataWithoutEmptyStrings
      : omit(dataWithoutEmptyStrings, ["external_section_id"]);
    return storeApi.courses.partial_update({ id: currentCourse.id, ...data }).then(() => {
      toastSuccessMessage(t("success.toasts.updated_course_settings"));
      onClose();
    });
  };

  return (
    <FormProvider {...form}>
      <form
        className={twMerge(
          "flex flex-col gap-2 text-sm font-medium leading-[1.3125rem] text-black-tint-10",
          className,
        )}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <LabeledTextInput
          {...form.register("title", { required: t("error.field_required") })}
          label={t("glossary.course_title")}
          required
        />
        <LabeledTextarea
          {...form.register("description")}
          label={t("glossary.course_description")}
          minRows={3}
        />

        <LabeledTextInput
          wrapperClassName={
            !canAuthorOrg && !currentCourse.external_section_id ? "hidden" : undefined
          }
          {...form.register("external_section_id", { disabled: !canAuthorOrg })}
          label={t("settings.external_section_id")}
          postLabel={<Tooltip text={t("settings.editable_admins_only")} />}
        />

        <div className="flex flex-col gap-1">
          <h3 className="mt-3 text-xs font-normal leading-[1.25rem] text-black-tint-20">
            {t("settings.lms_integration")}
          </h3>
          <div className="flex flex-row items-center justify-between">
            <LMSConnectionIndicator />
            {currentCourse.lms_section_id && (
              <LinkButton className="h-[32px] text-red" onClick={onHandleCourseUnlink}>
                {t("settings.unlink_course")}
              </LinkButton>
            )}
          </div>
        </div>

        <LinkButton className="h-[32px] text-red" onClick={onHandleCourseDelete}>
          {t("settings.delete_course")}
        </LinkButton>

        <SubmitOrCancelButtons className="pt-8" onCancel={onClose} />
      </form>
    </FormProvider>
  );
};
